import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { Mario } from '@pncl/mario-models';
import { Observable, Subject, map } from 'rxjs';
import { ISession, Session, SessionUser } from './models/session';
import { User } from './models/user';
import { UserService } from './services/user.service';
import { SpaceRepository } from './state/space.repository';
import { notEmpty } from './common/utils/common-util';

@Injectable({
  providedIn: 'root',
})
export class MarioService {
  private setMarioState$ = new Subject<{ contentId: string; state: unknown }>();
  public users$: Observable<Mario.User[]> = this.spaceRepo.activeSpace$.pipe(
    filterNil(),
    map<ISession, [ISession, CombinedUser[]]>((s) => [s, this.groupUsers(s)]),
    map(([s, users]) => users.map((u) => this.transformToMarioUser(s, u))),
  );

  public currentUser$: Observable<string> = this.userService.user.pipe(
    filterNil(),
    map((e) => e.user._id),
  );
  private initialStates: Map<string, Record<string, any>> = new Map();

  constructor(private userService: UserService, private spaceRepo: SpaceRepository) {}

  private groupUsers(space: ISession): CombinedUser[] {
    const userMap: Map<string, User | undefined> = new Map(
      space.populatedUsers.map((u) => [u._id, u]),
    );
    return space.users
      .map((spaceUser) => {
        const user = userMap.get(spaceUser._id);
        if (!user) {
          // if the join fails then we return nothing and filter
          return null;
        }
        return {
          ...user,
          ...spaceUser,
        };
      })
      .filter(notEmpty);
  }

  private transformToMarioUser(space: ISession, user: CombinedUser): Mario.User {
    return {
      _id: user._id,
      name: user.name,
      profilePicture: user.profile_image,
      role: this.extractMarioRole(space, user),
    };
  }

  private extractMarioRole(space: ISession, user: CombinedUser): Mario.UserRole {
    return Session.isOwnedByUser(space, user) ? Mario.UserRole.HOST : Mario.UserRole.PARTICIPANT;
  }

  public setMarioState(contentId: string, state: unknown): void {
    this.setMarioState$.next({ contentId, state });
  }

  public get marioStateChange$(): Observable<{ contentId: string; state: unknown }> {
    return this.setMarioState$.asObservable();
  }

  public getInitialState(contentId: string): Record<string, any> | undefined {
    return this.initialStates.get(contentId);
  }

  public setInitialState(contentId: string, state: Record<string, any>): void {
    this.initialStates.set(contentId, state);
  }

  public clearInitialState(contentId: string): void {
    this.initialStates.delete(contentId);
  }
}

type CombinedUser = User & SessionUser;
